// FieldConfig's canonical definition lives in the UI library (src/shared).
// Imported here so existing local references keep working, and re-exported below
// so the rest of the app can keep importing it from '../types'.
import type { FieldConfig } from 'shared';

// Section types from backend
export type SectionType =
  | 'APPLICATION_INFORMATION'
  | 'PROJECT_INFORMATION'
  | 'PROJECT_DESCRIPTION'
  | 'PROJECT_TEAM'
  | 'PROJECT_RESOURCES'
  | 'PROJECT_ATTACHMENTS'
  | 'PROJECT_SUMMARY';

// Section status types
export type SectionStatus = 'pending' | 'in_progress' | 'completed';

// Application status types
export type ApplicationStatus = 'draft' | 'submitted' | 'under_review' | 'selected' | 'not_selected';

// Field configuration for dynamic form fields (re-exported from the UI library)
export type { FieldConfig };

// ── Información de la convocatoria (contenido POR CONCURSO) ──────────────────
// DEV NOTE (backend): la sección "Información de la convocatoria" del form IR
// dejó de ser texto fijo del frontend — ahora se renderiza desde
// `SectionConfig.contestInfo` del template (sección APPLICATION_INFORMATION).
// Este contenido lo administra la DINV por concurso (títulos, párrafos, listas
// de recomendaciones, enlaces a plantillas/bases). Las "fechas clave" son en
// realidad atributos del Contest (openingDate, closingDate, resultsDate,
// projectStartDate, projectEndDate, finalReportDueDate): el backend debiera
// derivarlas de ahí, no duplicarlas como texto.
// Texto inline admite **negrita** y [enlace](url) (ver renderInline en
// ContestInformation.tsx).
export interface ContestInfoLink {
  label: string;
  url: string;
}

export interface ContestInfoItem {
  text: string; // admite **negrita** y [enlace](url)
  links?: ContestInfoLink[]; // plantillas descargables del ítem
  children?: ContestInfoItem[]; // sub-ítems (máx. 2 niveles de anidación)
}

export interface ContestInfoSection {
  title: string;
  paragraphs?: string[]; // párrafos antes de la lista
  items?: ContestInfoItem[];
  paragraphsAfter?: string[]; // párrafos después de la lista
  dates?: Array<{ label: string; value: string }>; // fechas clave
  tone?: 'default' | 'highlight'; // highlight = caja destacada (ej. Consultas)
}

export interface ContestInfoContent {
  title: string; // nombre completo del concurso
  intro: string[]; // párrafos de presentación (tarjeta destacada)
  sections: ContestInfoSection[];
}

// Section configuration
export interface SectionConfig {
  description: string;
  fields: Record<string, FieldConfig>;
  // Solo en APPLICATION_INFORMATION: contenido informativo del concurso.
  contestInfo?: ContestInfoContent;
}

// Section template data from backend
export interface SectionTemplateData {
  id: string;
  title: string;
  sectionType: SectionType;
  order: number;
  config: SectionConfig;
}

// Form template with sections
export interface FormTemplateWithSections {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  isPublished?: boolean;
  sections: SectionTemplateData[];
  deadline?: string;
  year?: number | null;
}

// Section-based application data
export interface SectionBasedApplication {
  id: string;
  userId: string;
  formTemplateId: string;
  applicationNumber: string;
  status: ApplicationStatus;
  answers: {
    sectionAnswers: Record<SectionType, any>;
    sectionStatus: Record<SectionType, SectionStatus>;
    lastSaved: string;
    complianceConfirmed: boolean;
  };
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  formTemplate: {
    id: string;
    name: string;
    description: string;
    sections?: SectionTemplateData[];
    deadline?: string;
    isActive: boolean;
    isPublished?: boolean;
    year?: number | null;
  };
  files?: any[];
  hasEvaluations?: boolean;
  awardNotifiedAt?: string | null;
}

// Legacy types kept for backward compatibility during transition
export type ResearcherType = 'consolidated' | 'emerging';
export type TeamType = 'consolidated' | 'emerging';

export type PublicationIdentifierType = 'DOI' | 'ISBN' | 'ROOT' | 'other';

export interface Publication {
  source?: 'sipa' | 'manual';
  title?: string;
  publishedAt?: string;
  // PA1: identificador genérico (tipo + valor), no solo DOI/ISBN.
  identifierType?: PublicationIdentifierType | null;
  identifierValue?: string | null;
  // PA4: enlace de acceso a la evidencia. En carga manual es el link que ingresa
  // el IR; para DOI se puede derivar (ver getPublicationLink en utils/publications).
  accessUrl?: string | null;
  // Archivo adjunto de la producción académica. Desde 2026-07-21 es la
  // EVIDENCIA DE ACCESO REQUERIDA de toda publicación (SIPA o manual): garantiza
  // que el evaluador pueda revisarla aunque el DOI sea de pago. El
  // identificador/accessUrl quedan como referencia. La completitud exige fileId
  // (ver checkPublicationsCompletion). NOTA dev fullstack: en el proto es un
  // fileId del mock store; el upload/almacenamiento real es del backend. En prod
  // puede auto-rellenarse desde CPA o detección OA (Unpaywall/OpenAlex), sin
  // omitir el archivo (ver docs/plan).
  fileId?: string | null;
}

export interface ConflictOfInterest {
  name: string;
  institution: string;
}

export interface SuggestedReviewer {
  name: string;
  institution: string;
  email?: string;
}

export interface IPCvData {
  investigatorId: string;
  investigatorName: string;
  role?: 'IR' | 'IP' | 'IA';
  cvNarrative: string;
  publications: Publication[];
}

export interface Researcher {
  name: string;
  email: string;
  run: string;
  documentType?: 'run' | 'passport';
  affiliation: string;
  facultadUC?: string;
  unidadAcademicaUC?: string;
  doctorateYear?: number; // opcional: IA no tiene grado de doctor
  doctorateMonth?: number; // 1-indexed (1=January…12=December); optional for same reason as doctorateYear
  type: ResearcherType;
  gender?: 'M' | 'F';
  // IR = Investigador Responsable (principalInvestigator field); IP = Investigador Principal (UC, con doctorado); IA = Investigador Asociado (UC, sin doctorado); COI = Co-investigador (externo a la UC)
  role?: 'IR' | 'IP' | 'IA' | 'COI';
  // BACKEND NOTE: 'institutionType' is a derived binary flag ('Nacional'|'Internacional').
  // The backend should compute it from `country` (=== 'Chile' → Nacional) rather than storing
  // it independently, to avoid inconsistencies. Consider deprecating this field.
  institutionType?: 'Nacional' | 'Internacional';
  // BACKEND NOTE: 'country' is required for all researchers (IP and COI).
  // It is optional here only due to prototype constraints — the form already collects it
  // for international members. The backend MUST enforce its presence and use it as the
  // authoritative field for institution → country reporting (REP-INST).
  // Without this field, the institution-by-country report cannot be computed for
  // international institutions beyond the binary nacional/internacional split.
  country?: string;
  doiFile?: string;
  commitmentLetter?: string;
}

export interface BudgetSummary {
  detailedFile?: string;
  montoSolicitado?: number;
  montoContrapartes?: number;
}

// Legacy application form interface - kept for backward compatibility
// File-related types for the upload system
export type FileStatus = 'UPLOADING' | 'COMPLETED' | 'FAILED';

export interface FileResponseDto {
  id: string;
  applicationId: string;
  sectionType: SectionType;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  status: FileStatus;
  uploadedAt: Date;
  downloadUrl: string;
}

// Evaluation types for researcher view (no evaluator information)
export interface EvaluationCriterion {
  score: number;
  maxScore: number;
  comments: string | null;
}

export interface ResearcherEvaluation {
  id: string;
  criteriaEvaluations: Record<string, EvaluationCriterion>;
  budgetComments: string | null;
  overallComments: string | null;
  conflictsDeclared: boolean;
  conflictDetails: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  finalScore: number | null;
}

export interface ApplicationWithEvaluations {
  id: string;
  applicationNumber: string;
  status: 'evaluated';
  submittedAt: string;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string; email: string };
  formTemplate: { id: string; name: string; description: string; deadline: string; isActive: boolean };
  answers: any;
  evaluations: ResearcherEvaluation[];
  files: any[];
  evaluationSummary: any;
}

export interface FileUploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'failed';
  error?: string;
  fileId?: string;
}

export interface FileUploadOptions {
  acceptedTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  allowMultiple?: boolean;
  sectionType: SectionType;
  applicationId?: string;
}

// Supported MIME types for file uploads
export const SUPPORTED_FILE_TYPES = {
  PDF: 'application/pdf',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  JPEG: 'image/jpeg',
  PNG: 'image/png',
} as const;

export const DEFAULT_ACCEPTED_TYPES = Object.values(SUPPORTED_FILE_TYPES);
export const DEFAULT_MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

export interface ApplicationForm {
  applicationNumber: string;
  projectTitle: string;
  interdisciplinaryProject: 'SI' | 'NO';
  transdisciplinaryProject: 'SI' | 'NO';
  genderApproachProject: 'SI' | 'NO';
  genderApproachOptions: string[];
  openScience: 'SI' | 'NO';
  rcer: string[];
  keywords: string;
  // Línea de investigación de la propuesta (texto libre; reemplaza al antiguo
  // "Área de Evaluación" de grupos FONDECYT). No confundir con la clasificación OCDE.
  evaluationArea: string;
  oecdArea: string;
  oecdSubarea: string;
  oecdDiscipline?: string;
  sdgGoals: string[];
  executionRegions: string[];
  // Identificador ORCID del Investigador Responsable (obs. DINV). En el
  // prototipo se captura aquí; en producción debería vivir en la base de
  // usuarios asociada al IR.
  orcid?: string;
  principalInvestigator: Researcher;
  coInvestigators: Researcher[];
  evaluationLine: string;
  suitableEvaluators: string[];
  unsuitableEvaluators: string[];
  executiveSummary: string;
  projectDocument?: string;
  budget: BudgetSummary;
  ipCvData: IPCvData[];
  suggestedReviewers: SuggestedReviewer[];
  hasConflictOfInterest: 'SI' | 'NO';
  conflictOfInterestList: ConflictOfInterest[];
  principalInvestigatorDoiFile?: string;
  coInvestigatorDoiFile?: string;
  institutionCommitmentLetters?: string[];
  status: ApplicationStatus;
  lastSaved: Date;
  submittedAt?: Date;
  // Declaraciones
  finalReportsUC: boolean;
  finalReportsANID: boolean;
  duplicateFunding: boolean;
  otherIncompatibilities: boolean;
  otherIncompatibilitiesDetails?: string;
  noIncompatibilities: boolean;
  informationDeclaration: boolean;
  singaporeDeclaration: boolean;
  // Justificación requerida cuando el equipo no tiene COI de institución extranjera
  internationalCoiJustification?: string;
  // Nuevos campos declarativos Avanza UC 2026
  // Vinculación con el medio: socios externos nacionales distintos de
  // universidades y centros de investigación.
  externalPartners?: 'SI' | 'NO';
  externalPartnerTypes?: string[]; // 'publico' | 'privado' | 'sociedad_civil'
  // Obs. DINV: se permite más de un nombre por tipo de actor → arreglo.
  externalPartnerNames?: {
    publico?: string[];
    privado?: string[];
    sociedad_civil?: string[];
  };
  globalEducationPact?: string[];
  usedAI?: 'SI' | 'NO';
  aiDetails?: string;
  // File references (IDs) for uploaded files
  uploadedFiles?: Record<SectionType, string[]>;
}

// Validation error types for backend error handling
export interface ValidationError {
  section: string;
  field: string;
  message: string;
  code: string;
}

export interface ValidationErrorResponse {
  message: string;
  errors: ValidationError[];
}

export interface ProcessedValidationError {
  field: string;
  message: string;
  code: string;
  section: string;
  sectionTitle: string;
  fieldLabel: string;
}
